import React from 'react';
import { Box, Typography, Button } from '@mui/material';
import { useTranslation } from 'react-i18next';

type EmptyStateProps = {
  title?: string;
  description?: string | React.ReactNode;
  actionLabel?: string;
  onAction?: () => void;
  image?: string;
};

export const EmptyState: React.FC<EmptyStateProps> = ({
  title,
  description,
  actionLabel,
  onAction,
  image = '/no_data.svg',
}) => {
  const { t } = useTranslation();

  return (
    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      py: { xs: 2, sm: 2.5, md: 3 },
      px: { xs: 2, sm: 3 },
      textAlign: 'center',
      minHeight: { xs: 'auto', sm: '400px' },
      maxHeight: 'calc(100vh - 200px)',
    }}>
      {/* SVG Image */}
      <Box
        component="img"
        src={image}
        alt="No data"
        sx={{
          width: { xs: 120, sm: 140, md: 160 },
          height: 'auto',
          mb: { xs: 2, sm: 2.5 },
          opacity: 0.8,
        }}
      />

      {/* Title */}
      <Typography
        variant="h6"
        sx={{
          fontWeight: 700,
          color: 'text.primary',
          mb: { xs: 1, sm: 1.5 },
          fontSize: (theme: any) => theme.typography.fontSizes.heading2,
        }}
      >
        {title || t("Oops! There's nothing here...")}
      </Typography>

      {/* Description */}
      {description && (
        <Box
          sx={{
            color: 'text.secondary',
            maxWidth: { xs: '100%', sm: 500, md: 600 },
            mb: actionLabel ? { xs: 2, sm: 2.5 } : 0,
            fontSize: (theme: any) => theme.typography.fontSizes.body,
            lineHeight: 1.5,
          }}
        >
          {typeof description === 'string' ? (
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {description}
            </Typography>
          ) : (
            description
          )}
        </Box>
      )}

      {/* Action Button */}
      {actionLabel && onAction && (
        <Button
          variant="contained"
          color="primary"
          onClick={onAction}
          sx={{
            mt: { xs: 1.5, sm: 2 },
            px: { xs: 2.5, sm: 3 },
            py: { xs: 0.75, sm: 1 },
            borderRadius: 2,
            fontSize: (theme: any) => theme.typography.fontSizes.body,
          }}
        >
          {actionLabel}
        </Button>
      )}
    </Box>
  );
};

